import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { SupportCaseManagementService } from '@/services/support/supportCaseManagementService';
import { 
  BaseSupportCase, 
  UpdateSupportCaseRequest,
  AddCommentRequest,
  SupportCaseFilters,
  AddSupportCaseDocumentRequest,
  RemoveSupportCaseDocumentRequest
} from '@/types/support';

type SupportCasesListCache = {
  cases?: BaseSupportCase[];
  total?: number;
  page?: number;
  totalPages?: number;
};

export function markAdminSupportCaseReadInCache(
  queryClient: QueryClient,
  caseId: string,
) {
  queryClient.setQueriesData<SupportCasesListCache>(
    { queryKey: ['supportCases'] },
    (old) => {
      if (!old?.cases) return old;
      return {
        ...old,
        cases: old.cases.map((c) =>
          c.id === caseId ? { ...c, hasUnread: false } : c,
        ),
      };
    },
  );
  void queryClient.invalidateQueries({ queryKey: ['supportUnreadSummary'] });
}

export function prefetchAdminSupportCase(
  queryClient: QueryClient,
  caseId: string,
) {
  return queryClient.prefetchQuery({
    queryKey: ['supportCase', 'admin', caseId],
    queryFn: () => SupportCaseManagementService.getSupportCase(caseId),
  });
}

export const useSupportCaseManagement = (
  filters?: SupportCaseFilters & { 
    page?: number; 
    limit?: number; 
    search?: string; 
  }, 
  caseId?: string
) => {
  const queryClient = useQueryClient();

  // Get all support cases query
  const supportCasesQuery = useQuery({
    queryKey: ['supportCases', filters],
    queryFn: async () => SupportCaseManagementService.getAllSupportCases(filters),
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: 'always',
  });

  // Single case — always refetch so backend marks as viewed
  const supportCaseQuery = useQuery({
    queryKey: ['supportCase', 'admin', caseId],
    queryFn: async () => SupportCaseManagementService.getSupportCase(caseId!),
    enabled: !!caseId,
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (!supportCaseQuery.isSuccess || !caseId) return;
    markAdminSupportCaseReadInCache(queryClient, caseId);
  }, [supportCaseQuery.isSuccess, supportCaseQuery.dataUpdatedAt, caseId, queryClient]);

  // Update support case mutation
  const updateSupportCaseMutation = useMutation({
    mutationFn: ({ caseId, updates }: { caseId: string; updates: UpdateSupportCaseRequest }) => 
      SupportCaseManagementService.updateSupportCase(caseId, updates),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['supportCases'] });
      queryClient.invalidateQueries({
        queryKey: ['supportCase', 'admin', variables.caseId],
      });
    },
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: (request: AddCommentRequest) => 
      SupportCaseManagementService.addComment(request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['supportCases'] });
      queryClient.invalidateQueries({
        queryKey: ['supportCase', 'admin', variables.supportCaseId],
      });
    },
  });

  // Add document mutation
  const addDocumentMutation = useMutation({
    mutationFn: (request: AddSupportCaseDocumentRequest) => 
      SupportCaseManagementService.addSupportCaseDocument(request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['supportCases'] });
      queryClient.invalidateQueries({
        queryKey: ['supportCase', 'admin', variables.supportCaseId],
      });
    },
  });

  // Remove document mutation
  const removeDocumentMutation = useMutation({
    mutationFn: (request: RemoveSupportCaseDocumentRequest) => 
      SupportCaseManagementService.removeSupportCaseDocument(request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['supportCases'] });
      queryClient.invalidateQueries({
        queryKey: ['supportCase', 'admin', variables.supportCaseId],
      });
    },
  });

  return {
    // Data
    supportCases: supportCasesQuery.data?.cases || [],
    supportCasesMeta: {
      total: supportCasesQuery.data?.total || 0,
      page: supportCasesQuery.data?.page || 1,
      totalPages: supportCasesQuery.data?.totalPages || 0,
    },
    supportCase: supportCaseQuery.data,
    
    // Loading states
    isLoading: supportCasesQuery.isLoading,
    isLoadingCase: supportCaseQuery.isLoading,
    
    // Error states
    error: supportCasesQuery.error?.message || null,
    caseError: supportCaseQuery.error?.message || null,
    
    // Mutations
    updateSupportCase: updateSupportCaseMutation.mutate,
    updateSupportCaseAsync: updateSupportCaseMutation.mutateAsync,
    addComment: addCommentMutation.mutate,
    addCommentAsync: addCommentMutation.mutateAsync,
    addDocument: addDocumentMutation.mutate,
    addDocumentAsync: addDocumentMutation.mutateAsync,
    removeDocument: removeDocumentMutation.mutate,
    removeDocumentAsync: removeDocumentMutation.mutateAsync,
    
    // Mutation states
    isUpdating: updateSupportCaseMutation.isPending,
    isAddingComment: addCommentMutation.isPending,
    isAddingDocument: addDocumentMutation.isPending,
    isRemovingDocument: removeDocumentMutation.isPending,
    
    // Mutation errors
    updateError: updateSupportCaseMutation.error?.message || null,
    addCommentError: addCommentMutation.error?.message || null,
    addDocumentError: addDocumentMutation.error?.message || null,
    removeDocumentError: removeDocumentMutation.error?.message || null,
    
    // Mutation success states
    updateSuccess: updateSupportCaseMutation.isSuccess,
    addCommentSuccess: addCommentMutation.isSuccess,
    addDocumentSuccess: addDocumentMutation.isSuccess,
    removeDocumentSuccess: removeDocumentMutation.isSuccess,
    
    // Refetch functions
    refetchSupportCases: supportCasesQuery.refetch,
    refetchSupportCase: supportCaseQuery.refetch,
    
    // Reset functions
    resetUpdate: updateSupportCaseMutation.reset,
    resetAddComment: addCommentMutation.reset,
    resetAddDocument: addDocumentMutation.reset,
    resetRemoveDocument: removeDocumentMutation.reset,
  };
};
